import React, { useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { Alert, Snackbar, IconButton, Box } from '@mui/material'
import Slide from '@mui/material/Slide'
import { AnalyticEvent } from 'src/analytics/adobe/types'
import useDeviceType from 'src/rsmCoreComponents/hooks/useDeviceType'
import { ToastSuccessIcon, CloseIcon } from '@rsmus/myrsm-icons'
import tokens from '../../../../styles/tokens.json'
import { useStyles } from '../Alert.styles'

const analyticEventType: AnalyticEvent = 'SuccessBannerPage_View'
export type CustomSuccessAlertProps = {
  message: string
  messageParams?: any
  header: string
  open: boolean
  close: () => void
  testId: string
  overrideToastMsgTimeLimit?: number | null
  dataAnalyticsId?: string
  disableAutoHide?: boolean
}

const CustomSuccessAlert = ({
  header,
  message,
  messageParams = null,
  open,
  close,
  testId,
  overrideToastMsgTimeLimit = null,
  dataAnalyticsId = '',
  disableAutoHide = false,
}: CustomSuccessAlertProps) => {
  const { t } = useTranslation()
  const { isMobile } = useDeviceType()
  const toastMsgTimelimit = disableAutoHide ? null : 8000
  const toastMsgSlideEnter = 1000
  const toastMsgSlideExit = 1000

  const classes = useStyles()

  const getAlertMessage = () => (
    <Box className="m-[1.5625rem]">
      <Box
        sx={(theme) => ({
          marginBottom: '0.3125rem',
          fontFamily: tokens.type.fontFamilies.preloBold,
          fontSize: '1.125rem',
          lineHeight: '1.75rem',
          textAlign: 'left',
          [theme.breakpoints.only('mobile')]: {
            fontSize: '1.5rem',
            paddingTop: '1rem',
          },
        })}
        data-testid={`${testId}_Header`}
        data-analytic-event={analyticEventType}
        data-analytic-id={dataAnalyticsId === '' ? testId : dataAnalyticsId}>
        {t(header)}
      </Box>
      <Box
        sx={(theme) => ({
          fontFamily: tokens.type.fontFamilies.preloBook,
          fontSize: '1.125rem',
          lineHeight: '1.5rem',
          textAlign: 'left',
          [theme.breakpoints.only('mobile')]: {
            fontSize: '1rem',
            paddingBottom: '1rem',
          },
        })}
        data-testid={`${testId}_Message`}
        data-analytic-event={analyticEventType}
        data-analytic-id={dataAnalyticsId === '' ? testId : dataAnalyticsId}>
        {`${t(message, messageParams)}`}
      </Box>
    </Box>
  )

  const getMobileView = () => (
    <Box
      sx={{
        alignContent: 'center',
        justifyItems: 'center',
        paddingTop: '1.5rem',
      }}>
      <ToastSuccessIcon
        size={isMobile ? '7.25rem' : '4rem'}
        color={tokens.colors.rsmGray.copy}
      />
      {getAlertMessage()}
    </Box>
  )

  useEffect(() => {
    if (open) {
      ;(window as any).pendo?.track(analyticEventType, {
        testId,
        dataAnalyticsId: dataAnalyticsId || testId,
        timestamp: new Date(),
      })
    }
  }, [open])

  let autoHideDuration = toastMsgTimelimit

  if (disableAutoHide) {
    autoHideDuration = null
  } else if (overrideToastMsgTimeLimit !== null) {
    autoHideDuration = overrideToastMsgTimeLimit
  }

  return (
    <Snackbar
      anchorOrigin={{
        vertical: 'top',
        horizontal: 'left',
      }}
      transitionDuration={{
        enter: toastMsgSlideEnter,
        exit: toastMsgSlideExit,
      }}
      TransitionComponent={Slide}
      autoHideDuration={autoHideDuration}
      open={open}
      onClose={(event, reason) => {
        if (disableAutoHide && reason === 'clickaway') {
          return
        }
        close()
      }}
      sx={{
        ...classes.successSnackbar,
        position: 'fixed',
        top: 0,
        left: 0,
      }}
      data-testid={`${testId}_Snackbar`}
      data-analytic-event={analyticEventType}
      data-analytic-id={dataAnalyticsId === '' ? testId : dataAnalyticsId}>
      <Alert
        aria-atomic="true"
        aria-live="assertive"
        severity="success"
        icon={
          !isMobile && (
            <Box sx={{ alignContent: 'center', paddingLeft: '3.5rem' }}>
              <ToastSuccessIcon color={tokens.colors.rsmGray.copy} />
            </Box>
          )
        }
        sx={{
          ...classes.successAlert,
          width: '100%',
          '.MuiAlert-action': {
            position: 'absolute',
            top: '0.313rem',
            right: '1.875rem',
          },
          '.MuiAlert-message': {
            width: '100%',
            textAlign: 'left',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'flex-start',
          },
        }}
        action={
          <IconButton
            size="small"
            aria-label="close"
            color="inherit"
            onClick={close}
            data-testid={`${testId}_CloseButton`}>
            <CloseIcon size={18} color={tokens.colors.rsmGray.copy} />
          </IconButton>
        }>
        {isMobile && getMobileView()}
        {!isMobile && getAlertMessage()}
      </Alert>
    </Snackbar>
  )
}

export default CustomSuccessAlert
